// src/user/Apps.js
import React, { useState } from 'react';
import { portalGradientTextSx } from '../styles/dashboardBrand';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Container,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import {
  PhoneAndroid,
  Apple,
  MoreHoriz,
  InfoOutlined,
} from '@mui/icons-material';
import dreameyVidsIcon from '../Icons/Dreamey Vids Icon.png';
import dreameyHubIcon from '../Icons/Dreamey Hub Icon.png';

const APP_STORE_DEVELOPER_URL = 'https://apps.apple.com/us/developer/imaginative-solutions/id1855143912';
const PLAY_STORE_DEVELOPER_URL = 'https://play.google.com/store/apps/developer?id=Imaginative+Solutions&hl=en';

function Apps() {
  const [supportInfoOpen, setSupportInfoOpen] = useState(false);

  const isAppleDevice = () => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || navigator.vendor || '';
    return /iPad|iPhone|iPod|Macintosh|Mac OS/i.test(ua);
  };

  const handleCardClick = () => {
    if (isAppleDevice()) {
      window.open(APP_STORE_DEVELOPER_URL, '_blank', 'noopener,noreferrer');
    } else {
      window.open(PLAY_STORE_DEVELOPER_URL, '_blank', 'noopener,noreferrer');
    }
  };

  // Main App - Dreamey Vids
  const dreameyVidsAndroid = {
    id: 'dreamey-vids-android',
    name: 'Dreamey Vids',
    description: 'Watch videos, explore destinations, sports highlights, and religious content',
    icon: dreameyVidsIcon,
    iconType: 'image',
    url: 'https://play.google.com/store/apps/details?id=com.dreamey.app&pcampaignid=web_share',
    color: '#4FACFE',
    platform: 'android',
  };

  const dreameyVidsIOS = {
    id: 'dreamey-vids-ios',
    name: 'Dreamey Vids',
    description: 'Watch videos, explore destinations, sports highlights, and religious content',
    icon: dreameyVidsIcon,
    iconType: 'image',
    url: 'https://apps.apple.com/pk/app/dreamey-vids/id6755639217',
    color: '#4FACFE',
    platform: 'ios',
  };

  const dreameyHubAndroid = {
    id: 'dreamey-hub-android',
    name: 'Dreamey Hub',
    description: 'Explore our game hub with cards, board games, running games, and more.',
    icon: dreameyHubIcon,
    iconType: 'image',
    url: PLAY_STORE_DEVELOPER_URL,
    color: '#f5576c',
    platform: 'android',
  };

  const dreameyHubIOS = {
    id: 'dreamey-hub-ios',
    name: 'Dreamey Hub',
    description: 'Explore our game hub with cards, board games, running games, and more.',
    icon: dreameyHubIcon,
    iconType: 'image',
    url: APP_STORE_DEVELOPER_URL,
    color: '#f5576c',
    platform: 'ios',
  };

  // Combined card: one card per app with name on top center, icon left, Android + iOS tabs
  // All links go to developer pages: App Store (iOS/Mac) or Play Store (Android/Web)
  const AppCardCombined = ({ androidApp, iosApp, matchSizeOnDesktop }) => {
    const app = androidApp; // name, icon, description same for both
    const isComingSoon = app.comingSoon;
    return (
      <Card
        elevation={2}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          border: '1px solid',
          borderColor: 'divider',
          cursor: isComingSoon ? 'default' : 'pointer',
          minHeight: { xs: 106, sm: 100 },
          height: { xs: 'auto', sm: 100 },
          ...(matchSizeOnDesktop && {
            width: { xs: 'auto', md: '100%' },
            minWidth: { md: 0 },
          }),
          '&:hover': {
            boxShadow: 6,
            borderColor: app.color || 'primary.main',
          },
        }}
        onClick={!isComingSoon ? handleCardClick : undefined}
      >
        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'stretch',
            gap: { xs: 1.25, sm: 2 },
            px: { xs: '10px', sm: '12px' },
            py: '10px',
            height: '100%',
            boxSizing: 'border-box',
            '&:last-child': { pb: '10px' },
          }}
        >
          {/* Left: Icon - full height of card, same size for all cards */}
          <Box
            sx={{
              width: { xs: 64, sm: 72 },
              minHeight: { xs: 84, sm: 80 },
              alignSelf: 'stretch',
              borderRadius: 2,
              bgcolor: (app.iconType === 'image' && app.icon) ? 'transparent' : (app.color || 'primary.main'),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              flexShrink: 0,
              overflow: 'hidden',
            }}
          >
            {app.iconType === 'image' && app.icon ? (
              <img src={app.icon} alt={`${app.name} icon`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <MoreHoriz sx={{ fontSize: 36 }} />
            )}
          </Box>
          {/* Right: App name + Android/iOS + description */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: { xs: 0.75, sm: 1 },
              minWidth: 0,
            }}
          >
            <Typography
              variant="h6"
              component="h3"
              sx={{
                fontWeight: 600,
                fontSize: { xs: '1rem', sm: '1.05rem' },
                lineHeight: 1.2,
                textAlign: 'left',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {app.name}
            </Typography>
            <Box sx={{ display: 'flex', gap: { xs: 0.75, sm: 1.5 }, flexWrap: 'wrap', justifyContent: 'flex-start' }}>
              {isComingSoon ? (
                <Chip
                  label="Coming Soon"
                  size="small"
                  sx={{
                    height: 28,
                    fontSize: '0.75rem',
                    bgcolor: '#f97316',
                    color: 'white',
                    '&:hover': { bgcolor: '#ea580c' },
                  }}
                />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.6, sm: 1 }, flexWrap: 'wrap' }}>
                  <Typography component="span" sx={{ fontSize: { xs: '0.82rem', sm: '0.9rem' }, fontWeight: 600, color: '#22c55e' }}>
                    Available on
                  </Typography>
                  <Chip
                    label="Android"
                    size="small"
                    icon={<PhoneAndroid sx={{ fontSize: 'inherit' }} />}
                    sx={{
                      height: { xs: 26, sm: 28 },
                      fontSize: { xs: '0.6rem', sm: '0.65rem' },
                      pointerEvents: 'none',
                      '& .MuiChip-icon': { fontSize: { xs: '0.8rem', sm: '0.9rem' } },
                    }}
                  />
                  <Chip
                    label="iOS"
                    size="small"
                    icon={<Apple sx={{ fontSize: 'inherit' }} />}
                    sx={{
                      height: { xs: 26, sm: 28 },
                      fontSize: { xs: '0.6rem', sm: '0.65rem' },
                      pointerEvents: 'none',
                      '& .MuiChip-icon': { fontSize: { xs: '0.8rem', sm: '0.9rem' } },
                    }}
                  />
                </Box>
              )}
            </Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                lineHeight: 1.6,
                fontSize: '0.65rem',
                display: 'none',
              }}
            >
              {app.description}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Dreamey Vids Section - before Please Support Us */}
      <Box sx={{ mb: { xs: 2, sm: 6 } }}>
        <Typography
          variant="h4"
          component="h2"
          gutterBottom
          sx={portalGradientTextSx({
            fontWeight: 700,
            mb: { xs: 1.5, sm: 3 },
            fontSize: { xs: '2rem', sm: '3rem', md: '3.75rem' },
            textAlign: 'center',
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.25))',
            '@keyframes popInOut': {
              '0%, 200%': { transform: 'scale(1)', opacity: 1.1 },
              '50%': { transform: 'scale(1.08)', opacity: 0.8 },
            },
            animation: 'popInOut 1.5s ease-in-out infinite',
          })}
        >
          Our Project
        </Typography>
        <Box sx={{ maxWidth: 380, mx: 'auto' }}>
          <AppCardCombined androidApp={dreameyVidsAndroid} iosApp={dreameyVidsIOS} />
        </Box>
      </Box>

      {/* Please Support Us */}
      <Box sx={{ mb: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: { xs: 1.5, sm: 3 } }}>
          <Typography
            variant="h4"
            component="h2"
            gutterBottom
            sx={portalGradientTextSx({
              fontWeight: 700,
              fontSize: { xs: '1.25rem', sm: '1.75rem', md: '2rem' },
              textAlign: 'center',
              mb: 0,
            })}
          >
            Please Support Dreamey Hub
          </Typography>
          <IconButton
            size="small"
            onClick={() => setSupportInfoOpen(true)}
            sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
            aria-label="Information about Dreamey Hub"
          >
            <InfoOutlined sx={{ fontSize: { xs: 20, sm: 24 } }} />
          </IconButton>
        </Box>
        <Dialog open={supportInfoOpen} onClose={() => setSupportInfoOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>About Dreamey Hub</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Dreamey Hub is not part of the engagement program. Downloading it helps support our work. Points,
              diamonds, and estimated earnings are virtual only and can be used within games.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSupportInfoOpen(false)} color="primary" autoFocus>
              Got it
            </Button>
          </DialogActions>
        </Dialog>
        <Box sx={{ maxWidth: 380, mx: 'auto' }}>
          <AppCardCombined
            androidApp={dreameyHubAndroid}
            iosApp={dreameyHubIOS}
          />
        </Box>
      </Box>

      {/* Additional Info Section */}
      <Box sx={{ mt: 6, textAlign: 'center' }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Need Help?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            If you encounter any issues downloading or installing our apps, please contact our support team.
            All apps are free to download and use.
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
}

export default Apps;
